<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify/framework'

import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import ParticipantFormRow from '@/components/adverseEvents/ParticipantFormRow.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import type { ISuggestionType } from '@/modules/improvements/types/ISuggestionType'
import { getFullName } from '@/utils/formatters'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    suggestionType?: ISuggestionType | null
  }>(),
  {
    modelValue: false,
    suggestionType: null,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'saved'): void
}>()

const { xs } = useDisplay()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()
const improvementsStore = useImprovementsStore()

const isModalOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => {
    if (!val) {
      onDialogDismissAttempt()
    } else {
      emit('update:modelValue', true)
    }
  },
})

const isEditMode = computed(() => !!props.suggestionType?.id)
/** Вид из ДаоМед — название нельзя менять, председатель и активность можно */
const isDaomedMain = computed(() => !!props.suggestionType?.is_daomed_main)

// Поля формы
const name = ref('')
const isActive = ref(true)

// Председатель
const selectedChairId = ref<string | null>(null)
const chairEmployee = ref<IEmployeeTreeItem | null>(null)
const chairModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const chairDisplayName = computed(() => {
  return chairEmployee.value ? chairEmployee.value.full_name : ''
})

// Эксперты / Заместители
const selectedDeputies = ref<{ id: string; full_name: string; position_name?: string }[]>([])
const deputiesModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const ppuExpertTree = ref<IEmployeeTreeDepartment[]>([])

const fetchPpuExpertsTree = async () => {
  const feedbackStore = useFeedbackStore()
  feedbackStore.isGlobalLoading = true
  try {
    ppuExpertTree.value = await improvementsStore.fetchPpuExpertsTree()
  } finally {
    feedbackStore.isGlobalLoading = false
  }
}

const showUnsavedChangesDialog = ref(false)

const getFormSnapshot = () => {
  return JSON.stringify({
    name: name.value.trim(),
    isActive: isActive.value,
    selectedChairId: selectedChairId.value,
    selectedDeputies: selectedDeputies.value.map(d => String(d.id)).sort(),
  })
}

const initialFormSnapshot = ref('')

const preselectedDeputies = computed(() => {
  return selectedDeputies.value.map(d => ({
    id: d.id,
    full_name: d.full_name,
    position_name: d.position_name,
  } as IEmployeeTreeItem))
})

watch(
  [() => props.modelValue, () => props.suggestionType],
  ([isOpen, typeObj]) => {
    if (!isOpen) return

    if (typeObj) {
      name.value = typeObj.name || ''
      isActive.value = typeObj.is_active ?? true

      if (typeObj.chair) {
        const c = typeObj.chair
        selectedChairId.value = c.id
        chairEmployee.value = {
          id: c.id,
          full_name: c.full_name || getFullName(c) || '',
          position_name: c.position_name || c.position?.name || '',
        } as IEmployeeTreeItem
      } else {
        selectedChairId.value = null
        chairEmployee.value = null
      }

      if (typeObj.deputies && Array.isArray(typeObj.deputies)) {
        selectedDeputies.value = typeObj.deputies.map(d => ({
          id: d.id,
          full_name: d.full_name || getFullName(d) || '',
          position_name: d.position_name || d.position?.name || '',
        }))
      } else {
        selectedDeputies.value = []
      }
    } else {
      name.value = ''
      isActive.value = true
      selectedChairId.value = null
      chairEmployee.value = null
      selectedDeputies.value = []
    }
    initialFormSnapshot.value = getFormSnapshot()
  },
  { immediate: true },
)

const hasFormChanges = (): boolean => {
  return getFormSnapshot() !== initialFormSnapshot.value
}

const closeModalDirectly = () => {
  emit('update:modelValue', false)
}

const handleCloseModal = () => {
  if (hasFormChanges()) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModalDirectly()
  }
}

const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal)

const handleCloseConfirm = (value: boolean) => {
  showUnsavedChangesDialog.value = false
  if (value) {
    closeModalDirectly()
  }
}

const openChairSelection = () => {
  chairModalRef.value?.open()
}

const onChairSelected = (emp: IEmployeeTreeItem) => {
  if (!emp) return
  selectedChairId.value = emp.id
  chairEmployee.value = emp
}

const clearChair = () => {
  selectedChairId.value = null
  chairEmployee.value = null
}

const openDeputiesSelection = async () => {
  if (isDaomedMain.value) return
  await fetchPpuExpertsTree()
  deputiesModalRef.value?.open()
}

const onMultipleDeputiesSelected = (employees: IEmployeeTreeItem[]) => {
  selectedDeputies.value = employees.map(emp => ({
    id: emp.id,
    full_name: emp.full_name,
    position_name: emp.position_name || '',
  }))
}

const removeDeputy = (id: string) => {
  if (isDaomedMain.value) return
  selectedDeputies.value = selectedDeputies.value.filter(d => String(d.id) !== String(id))
}

const handleSave = async () => {
  if (isDaomedMain.value && isEditMode.value && props.suggestionType?.id) {
    const result = await improvementsStore.updateSuggestionType(props.suggestionType.id, {
      is_active: isActive.value,
      chair: selectedChairId.value ? { id: selectedChairId.value } : null,
    })
    if (result) {
      emit('saved')
      closeModalDirectly()
    }
    return
  }

  if (!name.value.trim()) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Укажите наименование вида ППУ',
      time: 3000,
    })
    return
  }

  const payload = {
    name: name.value.trim(),
    chair: selectedChairId.value ? { id: selectedChairId.value } : null,
    deputies: selectedDeputies.value.map(d => ({ id: String(d.id) })),
    is_active: isActive.value,
  }

  let result = null
  if (isEditMode.value && props.suggestionType?.id) {
    result = await improvementsStore.updateSuggestionType(props.suggestionType.id, payload)
  } else {
    result = await improvementsStore.createSuggestionType(payload)
  }

  if (result) {
    emit('saved')
    closeModalDirectly()
  }
}
</script>

<template>
  <VDialog
    v-model="isModalOpen"
    max-width="900"
    persistent
    content-class="entity-form-menu"
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6" :class="xs ? 'px-6 py-4' : 'pa-6'">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">
              {{ isEditMode ? 'Редактирование вида ППУ' : 'Создание вида ППУ' }}
            </span>
          </div>
        </div>
        <CustomCloseButton :close-action="onDialogDismissAttempt" />
      </VCardTitle>

      <VCardText>
        <div class="d-flex flex-column gap-5 px-0">
          <!-- Наименование -->
          <VTextField
            v-model="name"
            variant="outlined"
            :clearable="!isDaomedMain"
            :disabled="isDaomedMain"
            placeholder="Введите наименование вида ППУ"
            persistent-placeholder
            @click:clear="name = ''"
          >
            <template #label>
              <span>Наименование<sup class="required-label">*</sup></span>
            </template>
          </VTextField>

          <!-- Председатель комиссии -->
          <VTextField
            :model-value="chairDisplayName"
            class="invisible-readonly cursor-pointer"
            variant="outlined"
            readonly
            clearable
            persistent-placeholder
            @click="openChairSelection"
            @click:clear="clearChair"
          >
            <template #label>
              <span>Председатель комиссии</span>
            </template>
          </VTextField>

          <!-- Заместители вида ППУ & Активность -->
          <div class="d-flex flex-column gap-2">
            <div class="d-flex align-center justify-space-between w-100 mb-2">
              <!-- Кнопка "Выбрать заместителей"
              <div class="d-flex align-center">
                <VBtn
                  :class="{ 'flex-grow-1': xs }"
                  variant="outlined"
                  prepend-icon="ri-user-add-line"
                  class="rounded-te-0 rounded-be-0"
                  @click="openDeputiesSelection"
                >
                  Выбрать заместителей
                </VBtn>
                <VIconBtn
                  color="primary"
                  class="rounded-ts-0 rounded-ds-0 rounded-te rounded-be"
                  icon="ri-arrow-down-s-line"
                  variant="outlined"
                  :rounded="false"
                  size="38"
                  @click="openDeputiesSelection"
                />
              </div>
              -->

              <VSwitch
                v-model="isActive"
                label="Вид ППУ активен"
                color="primary"
                hide-details
                density="compact"
                inset
              />
            </div>

            <!-- Список участников (ParticipantFormRow)
            <div v-if="selectedDeputies.length" class="d-flex flex-column gap-3 w-100">
              <ParticipantFormRow
                v-for="(deputy, index) in selectedDeputies"
                :id="String(deputy.id)"
                :key="deputy.id"
                :index="index + 1"
                :full_name="deputy.full_name"
                :employee_position_name="deputy.position_name"
                :is-ppu-participant="true"
                :show_delete_button="true"
                @delete="removeDeputy"
              />
            </div>
            -->
          </div>

          <div class="d-flex align-center mt-2" :class="xs ? 'flex-column' : 'justify-end'">
            <VBtn
              :block="xs"
              color="primary"
              @click="handleSave"
            >
              Сохранить
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>

    <!-- Дерево выбора Председателя (одиночный выбор) -->
    <EmployeeSelectionModal
      ref="chairModalRef"
      v-model="selectedChairId"
      :employees="apiStore.employees"
      title="Выберите председателя"
      @select="onChairSelected"
    />

    <!-- Дерево выбора Заместителей/Экспертов (множественный выбор) -->
    <EmployeeSelectionModal
      ref="deputiesModalRef"
      :employees="ppuExpertTree"
      :filtered="true"
      title="Выберите экспертов"
      multiple
      :selected-employees="preselectedDeputies"
      @select-multiple="onMultipleDeputiesSelected"
    />
  </VDialog>

  <!-- Диалог подтверждения закрытия при несохраненных изменениях -->
  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс изменения вида ППУ будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
  />
</template>
